import React, { useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ToastContainer } from "react-toastify";
import { saveAs } from "file-saver";
import close from "../../../images/icons/Icon2.png";
import downloadMasterIcon from "../../../assets/icons/download-master.svg";
import useSidebarHidden from "../../../common/useSidebarHidden.jsx";
import { APIEndpoint } from "../../../utils/axios";
import { formatDateByTimezone } from "../../../utils/commonFunctions";
import useToastAssist from "../../../utils/useToastAssist";
import * as XLSX from "xlsx";
import "../../../assets/styles/modal.scss";

const normalizeKey = (value) => String(value ?? "").trim().toLowerCase();

const getEntryQuantity = (entry) => {
  if (!entry) return null;
  const qty = entry.quantity ?? entry.qty ?? entry.count ?? entry.retrievedQty;
  if (qty === null || qty === undefined || qty === "") return null;
  return String(qty);
};

const entriesMatchTrolley = (entry, trolley) => {
  if (!entry || !trolley) return false;
  if (entry.trolleyKey && trolley.trolleyKey) {
    return String(entry.trolleyKey) === String(trolley.trolleyKey);
  }
  if (entry.trolleyName && trolley.trolleyName) {
    return normalizeKey(entry.trolleyName) === normalizeKey(trolley.trolleyName);
  }
  return false;
};

const getQuantityFromEntries = (entries, trolley) => {
  if (!entries?.length) return null;
  const match = entries.find((entry) => entriesMatchTrolley(entry, trolley));
  return getEntryQuantity(match);
};

const findSavedItem = (item, savedItems) => {
  if (!item || !savedItems?.length) return null;
  return (
    savedItems.find(
      (entry) =>
        item.itemCode &&
        entry.itemCode &&
        normalizeKey(entry.itemCode) === normalizeKey(item.itemCode),
    ) ||
    savedItems.find(
      (entry) =>
        item.itemId != null &&
        (entry.itemId === item.itemId || entry.id === item.itemId),
    ) ||
    savedItems.find(
      (entry) =>
        item.itemName &&
        entry.itemName &&
        normalizeKey(entry.itemName) === normalizeKey(item.itemName),
    )
  );
};

const buildRowCounts = (item, trolleys, savedItem) =>
  (trolleys || []).reduce((acc, trolley) => {
    const qty =
      getQuantityFromEntries(savedItem?.trolleys, trolley) ??
      getQuantityFromEntries(item?.trolleys, trolley);
    acc[trolley.trolleyKey] = qty ?? "";
    return acc;
  }, {});

const mapItemsToRows = (items, trolleys, savedItems) =>
  (items || []).map((item) => ({
    id: item.itemId ?? item.id ?? item.itemCode,
    itemName: item.itemName,
    itemCode: item.itemCode,
    unit: item.unit,
    counts: buildRowCounts(item, trolleys, findSavedItem(item, savedItems)),
  }));

const parseApiError = (error, fallback) => {
  try {
    const parsed = JSON.parse(error.message);
    return parsed?.data?.msg || parsed?.data?.message || fallback;
  } catch (_) {
    return fallback;
  }
};

const findHeaderRowIndex = (sheetRows) =>
  sheetRows.findIndex((row) =>
    (row || []).some((cell) => {
      const key = normalizeKey(cell);
      return key === "item code" || key === "itemcode";
    }),
  );

const findColumnIndex = (headerRow, names) =>
  headerRow.findIndex((cell) => names.includes(normalizeKey(cell)));

const findTrolleyForHeader = (header, trolleys) => {
  const key = normalizeKey(header);
  if (!key || key === "item code" || key === "itemcode" || key === "item name" || key === "itemname") {
    return null;
  }
  return (
    trolleys.find(
      (trolley) =>
        normalizeKey(trolley.trolleyName) === key ||
        normalizeKey(trolley.trolleyKey) === key,
    ) ||
    (key === "unassigned"
      ? trolleys.find(
          (trolley) => normalizeKey(trolley.trolleyName) === "unassigned",
        )
      : null)
  );
};

const parseQuantity = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const digits = String(value).replace(/[^0-9]/g, "");
  if (digits === "") return null;
  return digits;
};

const applyTemplateToRows = (sheetRows, currentRows, trolleys) => {
  const headerRowIndex = findHeaderRowIndex(sheetRows);
  if (headerRowIndex < 0) {
    throw new Error("Could not find an Item Code column in the uploaded file.");
  }

  const headerRow = sheetRows[headerRowIndex] || [];
  const itemCodeIndex = findColumnIndex(headerRow, ["item code", "itemcode"]);
  const itemNameIndex = findColumnIndex(headerRow, ["item name", "itemname"]);
  if (itemCodeIndex < 0 && itemNameIndex < 0) {
    throw new Error("The uploaded file must include Item Code or Item Name.");
  }

  const trolleyColumns = headerRow
    .map((header, index) => ({
      index,
      trolley: findTrolleyForHeader(header, trolleys),
    }))
    .filter((column) => column.trolley);

  if (!trolleyColumns.length) {
    throw new Error(
      "No trolley columns in the file matched the retrieval grid.",
    );
  }

  const rowsByCode = new Map(
    currentRows
      .filter((row) => row.itemCode)
      .map((row) => [normalizeKey(row.itemCode), row.id]),
  );
  const rowsByName = new Map(
    currentRows
      .filter((row) => row.itemName)
      .map((row) => [normalizeKey(row.itemName), row.id]),
  );

  const updates = new Map();
  let matchedItems = 0;

  sheetRows.slice(headerRowIndex + 1).forEach((sheetRow) => {
    if (!sheetRow || !sheetRow.length) return;
    const itemCode =
      itemCodeIndex >= 0 ? normalizeKey(sheetRow[itemCodeIndex]) : "";
    const itemName =
      itemNameIndex >= 0 ? normalizeKey(sheetRow[itemNameIndex]) : "";
    const rowId =
      (itemCode && rowsByCode.get(itemCode)) ||
      (itemName && rowsByName.get(itemName));
    if (!rowId) return;

    matchedItems += 1;
    const nextCounts = { ...(updates.get(rowId) || {}) };
    trolleyColumns.forEach(({ index, trolley }) => {
      const quantity = parseQuantity(sheetRow[index]);
      if (quantity === null) return;
      nextCounts[trolley.trolleyKey] = quantity;
    });
    updates.set(rowId, nextCounts);
  });

  if (!matchedItems) {
    throw new Error("No rows in the file matched items on the retrieval grid.");
  }

  const nextRows = currentRows.map((row) => {
    const counts = updates.get(row.id);
    if (!counts) return row;
    return { ...row, counts: { ...row.counts, ...counts } };
  });

  return { nextRows, matchedItems, trolleyCount: trolleyColumns.length };
};

export const AddRetrieval = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { state } = useLocation();
  const { cateringGroupId } = useParams();
  const [searchParams] = useSearchParams();
  const reconData = state?.reconData;
  const warehouseId =
    searchParams.get("warehouseId") || reconData?.warehouse?.warehouseId;
  const flightDate =
    searchParams.get("flightDate") ||
    formatDateByTimezone(reconData?.date || reconData?.lastSector?.flightDate);
  const retrievalIdFromQuery =
    searchParams.get("retrievalId") || reconData?.retrieval?.id || "";
  const sideBarHidden = useSidebarHidden(".content-container");
  const { toastError, toastSuccess } = useToastAssist();

  const [trolleys, setTrolleys] = useState([]);
  const [rows, setRows] = useState([]);
  const [formSector, setFormSector] = useState(null);
  const [retrievalId, setRetrievalId] = useState(retrievalIdFromQuery);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const goToModuleHome = () => navigate("/inventory/reconciliations");

  useEffect(() => {
    let cancelled = false;

    const loadForm = async () => {
      if (!cateringGroupId || !warehouseId || !flightDate) {
        const message =
          "Unable to load retrieval form. Missing catering group, warehouse, or flight date.";
        setLoadError(message);
        setLoading(false);
        toastError(message);
        return;
      }

      setLoading(true);
      setLoadError("");
      try {
        const query = new URLSearchParams({
          cateringGroupId,
          flightDate,
        });
        if (retrievalIdFromQuery) {
          query.set("retrievalId", retrievalIdFromQuery);
        }
        const res = await APIEndpoint.get(
          `warehouses/${warehouseId}/manualRecons/retrieval/form?${query.toString()}`,
        );
        if (cancelled) return;

        const payload = res?.trolleys || res?.items || res?.retrieval
          ? res
          : res?.result || res?.data || res;
        const nextTrolleys = payload?.trolleys || [];
        let savedItems =
          payload?.retrieval?.items ||
          reconData?.retrieval?.items ||
          [];

        if (retrievalIdFromQuery && !savedItems.length) {
          try {
            const existing = await APIEndpoint.get(
              `warehouses/${warehouseId}/manualRecons/retrieval/${retrievalIdFromQuery}`,
            );
            if (!cancelled) {
              savedItems =
                existing?.retrieval?.items ||
                existing?.items ||
                existing?.result?.items ||
                [];
            }
          } catch (_) {
            savedItems = savedItems;
          }
        }

        const formItems = payload?.items?.length
          ? payload.items
          : savedItems.length
            ? savedItems
            : [];

        if (cancelled) return;
        setTrolleys(nextTrolleys);
        setRows(mapItemsToRows(formItems, nextTrolleys, savedItems));
        setFormSector(payload?.sector || null);
        setRetrievalId(payload?.retrieval?.id || retrievalIdFromQuery);
      } catch (error) {
        if (cancelled) return;
        const message = parseApiError(error, "Failed to load retrieval form.");
        setLoadError(message);
        setTrolleys([]);
        setRows([]);
        toastError(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadForm();
    return () => {
      cancelled = true;
    };
  }, [cateringGroupId, warehouseId, flightDate, toastError]);

  const onCountChange = (itemId, trolleyKey, value) => {
    const count = value.replace(/[^0-9]/g, "");
    setRows((prev) =>
      prev.map((row) =>
        row.id === itemId
          ? { ...row, counts: { ...row.counts, [trolleyKey]: count } }
          : row,
      ),
    );
  };

  const onUploadClose = () => {
    setShowUpload(false);
    setUploadedFile(null);
  };

  const onUploadFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setUploadedFile(file);
  };

  const onUploadContinue = async () => {
    if (!uploadedFile) return;
    setParsing(true);
    try {
      const buffer = await uploadedFile.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      if (!sheetName) {
        throw new Error("The uploaded file does not contain a worksheet.");
      }
      const sheetRows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
        header: 1,
        defval: "",
        raw: true,
      });
      const { nextRows, matchedItems } = applyTemplateToRows(
        sheetRows,
        rows,
        trolleys,
      );
      setRows(nextRows);
      toastSuccess(
        `Loaded quantities for ${matchedItems} item${matchedItems === 1 ? "" : "s"}.`,
      );
      onUploadClose();
    } catch (error) {
      toastError(
        error?.message || "Failed to parse the uploaded retrieval template.",
      );
    } finally {
      setParsing(false);
    }
  };

  const downloadTemplate = async () => {
    if (!cateringGroupId || !warehouseId || !flightDate) {
      toastError(
        "Unable to download template. Missing catering group, warehouse, or flight date.",
      );
      return;
    }

    const operationDate =
      formatDateByTimezone(formSector?.operationDate) || flightDate;

    setDownloading(true);
    try {
      const header = {
        ...APIEndpoint.getHeaders().headers,
        Accept:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      };
      const queryParams = new URLSearchParams({
        cateringGroupId,
        flightDate,
      });
      if (operationDate) queryParams.append("operationDate", operationDate);

      const res = await fetch(
        APIEndpoint.getFullPath(
          `warehouses/${warehouseId}/manualRecons/retrieval/template?${queryParams.toString()}`,
        ),
        { method: "GET", headers: header, credentials: "include" },
      );
      if (!res.ok) {
        const errJson = await res.json().catch(() => null);
        toastError(
          errJson?.message ||
            errJson?.msg ||
            "Failed to download retrieval template.",
        );
        return;
      }
      const blob = await res.blob();
      saveAs(blob, "retrieval-template.xlsx");
    } catch (error) {
      toastError("Failed to download retrieval template.");
    } finally {
      setDownloading(false);
    }
  };

  const buildSubmitPayload = () => {
    const operationDate =
      formatDateByTimezone(formSector?.operationDate) || flightDate;
    return {
      cateringGroupId,
      flightDate,
      operationDate,
      items: rows
        .filter((row) => row.itemCode)
        .map((row) => ({
          itemCode: row.itemCode,
          trolleys: trolleys.map((trolley) => ({
            trolleyKey: trolley.trolleyKey,
            quantity: Number(row.counts?.[trolley.trolleyKey] || 0),
          })),
        })),
    };
  };

  const onSubmitRetrieval = async () => {
    if (!cateringGroupId || !warehouseId || !flightDate) {
      toastError(
        "Unable to submit retrieval. Missing catering group, warehouse, or flight date.",
      );
      return;
    }
    const body = buildSubmitPayload();
    if (!body.items.length) {
      toastError("There are no items to submit.");
      return;
    }

    setSubmitting(true);
    try {
      const path = retrievalId
        ? `warehouses/${warehouseId}/manualRecons/retrieval/${retrievalId}`
        : `warehouses/${warehouseId}/manualRecons/retrieval`;
      const res = retrievalId
        ? await APIEndpoint.put(path, JSON.stringify(body))
        : await APIEndpoint.post(path, JSON.stringify(body));
      if (res?.success === 0 || res?.success === false) {
        toastError(
          res?.message ||
            res?.msg ||
            (retrievalId
              ? "Failed to update retrieval."
              : "Failed to submit retrieval."),
        );
        return;
      }
      toastSuccess(
        res?.message ||
          (retrievalId
            ? "Retrieval updated successfully."
            : "Retrieval submitted successfully."),
      );
      setShowConfirm(false);
      goToModuleHome();
    } catch (error) {
      toastError(parseApiError(error, retrievalId ? "Failed to update retrieval." : "Failed to submit retrieval."));
    } finally {
      setSubmitting(false);
    }
  };

  const fileName = uploadedFile?.name || t("filters.chooseXlsFile");
  const isEdit = Boolean(retrievalId);
  const retrievalTitle =
    reconData?.cateringGroupName ||
    reconData?.rotationCode ||
    (formSector
      ? `${formSector.flightNumber || ""} ${formSector.origin || ""}-${formSector.destination || ""}`.trim()
      : "");

  return (
    <div className="op-aircraft-container flexCol full-flex ag-theme-alpine-dark">
      <div className="flexRow alignItemsCenter margBot10 justifyContentSpaceBetween margTop10">
        <p className="h4">
          {isEdit ? "Edit Retrieval" : "Add Retrieval"}
          {retrievalTitle ? ` - ${retrievalTitle}` : ""}
        </p>
        <div className="flexRow alignItemsCenter">
          <button
            className="add-item-button margRight10 "
            onClick={() => setShowUpload(true)}
          >
            + Upload
          </button>
          <button onClick={goToModuleHome}>
            <img src={close} alt="close" data-icon="close" />
          </button>
        </div>
      </div>
      <div className="add-retrieval-table-wrap sm-scrollbar margTop30">
        {loading ? (
          <p className="h5">Loading retrieval form...</p>
        ) : loadError ? (
          <p className="h5">{loadError}</p>
        ) : (
          <table className="add-retrieval-table">
            <thead>
              <tr className="header-white">
                <th>Item Name</th>
                {trolleys.map((trolley, trolleyIndex) => (
                  <th
                    key={trolley.trolleyKey || `${trolley.trolleyName}-${trolleyIndex}`}
                    title={trolley.trolleyName}
                  >
                    {trolley.trolleyName}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={Math.max(trolleys.length + 1, 1)}>No items</td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td title={row.itemCode || row.itemName}>{row.itemName}</td>
                    {trolleys.map((trolley, trolleyIndex) => (
                      <td
                        key={trolley.trolleyKey || `${trolley.trolleyName}-${trolleyIndex}`}
                        className="no-padding"
                      >
                        <input
                          type="number"
                          min="0"
                          className="edit-input trollyQtyInp"
                          aria-label={`${row.itemName} ${trolley.trolleyName}`}
                          value={row.counts[trolley.trolleyKey]}
                          onChange={(e) =>
                            onCountChange(
                              row.id,
                              trolley.trolleyKey,
                              e.target.value,
                            )
                          }
                        />
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
      <div className="flexRow justifyContentFlexEnd add-retrieval-submit-bar">
        <button
          className="add-item-button"
          disabled={loading || submitting || !rows.length}
          onClick={() => setShowConfirm(true)}
        >
          {isEdit ? "Update Retrieval" : "Submit Retrieval"}
        </button>
      </div>
      {showConfirm && (
        <div className="overlay add-retrieval-upload-overlay" role="dialog">
          <div
            className={`${sideBarHidden ? "dialog-fs2" : "dialog"} sm-scrollbar`}
          >
            <div className="dialog__content">
              <div className="dialog-header flexRow alignItemsCenter width100 content-tabs">
                <h2 className="dialog__title subheading-text-medium fontMedium justifyContentCenter alignItemsCenter margBottom0 width50">
                  {isEdit ? "Update Retrieval" : "Submit Retrieval"}
                </h2>
                <button
                  className="close-btn-ht"
                  disabled={submitting}
                  onClick={() => setShowConfirm(false)}
                >
                  <img src={close} alt="close" data-icon="close" />
                </button>
              </div>
              <hr />
              <div className="dialog__footer flexRow justifyContentSpaceEvenly flexWrap margLeft10">
                <h6>
                  {isEdit
                    ? "Are you sure you want to update this retrieval? Make sure that the item counts are correct before proceeding."
                    : "Are you sure you want to submit this retrieval? Submitting a retrieval is irreversible. Make sure that everything is in order before proceeding."}
                </h6>
              </div>
              <div className="dialog__footer flexRow justifyContentSpaceEvenly">
                <button
                  className="cancel-button"
                  disabled={submitting}
                  onClick={() => setShowConfirm(false)}
                >
                  No
                </button>
                <button
                  className="done-button"
                  disabled={submitting}
                  onClick={onSubmitRetrieval}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showUpload && (
        <div className="overlay add-retrieval-upload-overlay" role="dialog">
          <div
            className={`${sideBarHidden ? "dialog-fs2" : "dialog"} sm-scrollbar`}
          >
            <div className="dialog__content ">
              <div className="dialog-header flexRow alignItemsCenter width100 content-tabs">
                <h2 className="dialog__title subheading-text-medium fontMedium justifyContentCenter alignItemsCenter margBottom0 width50">
                  Upload Retrieval File
                </h2>
                <button className="close-btn-ht" onClick={onUploadClose}>
                  <img src={close} alt="close" data-icon="close" />
                </button>
              </div>
              <hr />
              <div className="dialog__body_container flexRow mt-4 button-align justifyContentSpaceBetween alignItemsCenter flexWrap">
                <div className="file-upload input-group col-md-8 col-xs-12 content-tabs">
                  <span className="btn btn-primary btn-file">
                    <label htmlFor="file-upload">{t("filters.browse")}</label>
                    <input
                      id="file-upload"
                      type="file"
                      name="file"
                      accept=".xlsx,.xls"
                      onChange={onUploadFileChange}
                    />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={fileName}
                    value={uploadedFile ? uploadedFile.name : ""}
                    readOnly
                  />
                </div>
                <div className="flexRow alignItemsCenter add-retrieval-template-download">
                  <button
                    type="button"
                    title="Download Template"
                    disabled={downloading}
                    onClick={downloadTemplate}
                    style={
                      downloading
                        ? { opacity: 0.5, cursor: "not-allowed" }
                        : undefined
                    }
                  >
                    <img
                      className="icon-size-min"
                      alt="downloadTemplate"
                      src={downloadMasterIcon}
                    />
                  </button>
                </div>
              </div>
              <div className="flexRow justifyContentCenter alignItemsCenter margTop10">
                <button
                  className="done-button"
                  disabled={!uploadedFile || parsing}
                  onClick={onUploadContinue}
                >
                  {t("buttons.continue")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ToastContainer />
    </div>
  );
};
